import { getErrorMessage } from '@us/web/shared/utils';
import { inject, Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { tuiIsPresent } from '@taiga-ui/cdk';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { SerializablePosition } from '@us/web/shared/data-access';
import { pick } from 'lodash';
import { catchError, concatMap, EMPTY, filter, from, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { GoogleMapsService } from '../services';
import { PositionActions } from './position.actions';
import { LocationRequestComponent } from '@us/web/position/feature';

@Injectable()
export class PositionEffects {
    private readonly actions$ = inject(Actions);
    private readonly dialogService = inject(TuiDialogService);
    private readonly injector = inject(Injector);
    private readonly googleMapsService = inject(GoogleMapsService);

    requestPosition$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PositionActions.requestPosition),
            mergeMap(({ force }) =>
                (force
                    ? of({ accepted: true })
                    : this.dialogService.open<{
                          accepted: boolean;
                      }>(new PolymorpheusComponent(LocationRequestComponent, this.injector), {
                          closeable: false,
                          dismissible: false
                      })
                ).pipe(
                    switchMap(({ accepted }) => {
                        if (!accepted) {
                            return EMPTY;
                        }
                        if (navigator.geolocation) {
                            const currentLocation$: Observable<GeolocationPosition> = from(
                                new Promise<GeolocationPosition>((resolve, reject) =>
                                    navigator.geolocation.getCurrentPosition(position => {
                                        resolve(position);
                                    }, reject)
                                )
                            );
                            return currentLocation$.pipe(
                                map(position => {
                                    const serializablePosition: SerializablePosition = {
                                        lat: position.coords.latitude,
                                        lng: position.coords.longitude,
                                        accuracy: position.coords.accuracy,
                                        timestamp: position.timestamp
                                    };

                                    return PositionActions.requestPositionSuccess({ position: serializablePosition });
                                })
                            );
                        } else {
                            console.log('Unable to retrieve location');
                            return EMPTY;
                        }
                    })
                )
            )
        );
    });

    requestPositionSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PositionActions.requestPositionSuccess),
            map(({ position }) => position),
            filter(tuiIsPresent),
            switchMap(position => of(PositionActions.getGeocode(pick(position, ['lat', 'lng']))))
        );
    });

    getGeocode$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PositionActions.getGeocode),
            concatMap(({ lat, lng }) => {
                return this.googleMapsService.getGeocode({ lat, lng }).pipe(
                    map(geocode => PositionActions.getGeocodeSuccess({ geocode })),
                    catchError(error => of(PositionActions.getGeocodeFailure({ error: getErrorMessage(error) })))
                );
            })
        );
    });
}
